import { TestIdDomain } from 'constants/testIds';

export const CommandCenterWidgetId = {
  RECAP_HERO: 'RECAP_HERO',
  ACTION_CENTER: 'ACTION_CENTER',
  STATUS: 'STATUS',
  SHOP_PIPELINE: 'SHOP_PIPELINE',
  RELATIONSHIP: 'RELATIONSHIP',
  NEWS: 'NEWS',
  FILE_QUALITY: 'FILE_QUALITY',
  CLIENT_PROTECTION: 'CLIENT_PROTECTION',
  TRUST_FOOTER: 'TRUST_FOOTER',
  KPI_STRIP: 'KPI_STRIP',
  PIPELINE_FUNNEL: 'PIPELINE_FUNNEL',
  TURN_TIME_TREND: 'TURN_TIME_TREND',
  TURN_TIME_STAGES: 'TURN_TIME_STAGES',
  PRICING_BAND: 'PRICING_BAND',
  SLA_GAUGE: 'SLA_GAUGE',
  PRIORITY_QUEUE: 'PRIORITY_QUEUE',
  ACTIVITY_FEED: 'ACTIVITY_FEED',
  COMPANY_HERO: 'COMPANY_HERO',
  COMPANY_VOLUME: 'COMPANY_VOLUME',
  APPROVAL_SCOPE: 'APPROVAL_SCOPE',
  CONDITION_HEATMAP: 'CONDITION_HEATMAP',
  QUEUE_DEPTH: 'QUEUE_DEPTH',
  EPO_WATCH: 'EPO_WATCH',
  DISCLOSURE_TIMING: 'DISCLOSURE_TIMING',
  CLOSING_THIS_WEEK: 'CLOSING_THIS_WEEK',
};

export const CommandCenterWidgetPlacement = {
  GRID: 'GRID',
  FOOTER: 'FOOTER',
};

// Dashboard grid (DEV-4096): widgets lay out on a 12-column grid. A widget spans
// either the full row or half of it; two half-width widgets pair into one row.
export const COMMAND_CENTER_FULL_COL_SPAN = 12;
export const COMMAND_CENTER_HALF_COL_SPAN = 6;

// Keyboard activation (DEV-4290): the keys that activate a clickable deep-link row,
// matching native button semantics — Enter and Space (`event.key === ' '`).
export const ROW_ACTIVATION_KEYS = ['Enter', ' '];

// Rescope (DEV-4041 + DEV-4096): a company-level user (lender staff with no master
// sub-company) sees whole-company data and the broker-channel-only zones are
// hidden. These widget zones are entirely broker-channel — they drop out of the
// composed widget list in company-level mode.
// DEV-4096 (v4 Owner layout): STATUS, ACTION_CENTER, SHOP_PIPELINE, and
// RELATIONSHIP are all broker-specific zones removed from the company-level Owner
// view per the v4 mockup — the company-level layout uses KPI_STRIP, PIPELINE_FUNNEL,
// TURN_TIME_TREND, PRICING_BAND, FILE_QUALITY, PRIORITY_QUEUE, SLA_GAUGE,
// ACTIVITY_FEED, and NEWS (announcements) instead.
export const COMMAND_CENTER_COMPANY_LEVEL_HIDDEN_WIDGET_IDS = [
  CommandCenterWidgetId.RECAP_HERO,
  CommandCenterWidgetId.CLIENT_PROTECTION,
  CommandCenterWidgetId.STATUS,
  CommandCenterWidgetId.ACTION_CENTER,
  CommandCenterWidgetId.SHOP_PIPELINE,
  CommandCenterWidgetId.RELATIONSHIP,
  CommandCenterWidgetId.APPROVAL_SCOPE,
  // EPO / clawback watch is a broker-owner surface (early-payoff exposure on the
  // broker's own funded book) — hidden for a company-level lender-staff viewer (DEV-4176).
  CommandCenterWidgetId.EPO_WATCH,
];

// Widgets that are company-level only and must be hidden for broker (sub-company)
// users. COMPANY_HERO is the gradient banner at the top of the company Owner view;
// brokers see RECAP_HERO instead. ACTIVITY_FEED is the company-wide team-activity
// card that replaces ACTION_CENTER in the company-level triplet row. COMPANY_VOLUME
// is the funded-units / bar-chart card that replaces RELATIONSHIP in the quality+volume row.
export const COMMAND_CENTER_BROKER_HIDDEN_WIDGET_IDS = [
  CommandCenterWidgetId.COMPANY_HERO,
  CommandCenterWidgetId.ACTIVITY_FEED,
  CommandCenterWidgetId.COMPANY_VOLUME,
];

export const ApprovalStatus = {
  APPROVED: 'APPROVED',
  NOT_APPROVED: 'NOT_APPROVED',
  PENDING_REVIEW: 'PENDING_REVIEW',
};

export const AlertSeverity = {
  ACTION_REQUIRED: 'ACTION_REQUIRED',
  HEADS_UP: 'HEADS_UP',
  INFORMATIONAL: 'INFORMATIONAL',
};

// Turn-time target (App→CTC) used as the dashed reference line in TurnTimeTrendZone.
export const TURN_TIME_TARGET_DAYS = 20;

// Minimum file count before a turn-time week's median is considered reliable.
export const TURN_TIME_MIN_FILE_COUNT = 3;

// Priority queue preview cap — how many ranked items to show before "View all".
export const COMMAND_CENTER_PRIORITY_QUEUE_PREVIEW_COUNT = 6;

// Dashboard preview caps — how many rows each "at a glance" zone shows before
// the "View all N →" footer link. Matches the design goal of a scannable
// one-screen dashboard (not an unbounded list dump).
export const COMMAND_CENTER_ATTENTION_PREVIEW_COUNT = 5;
export const COMMAND_CENTER_PIPELINE_PREVIEW_COUNT = 8;

export const HOURS_PER_DAY = 24;
export const LICENSE_WARNING_THRESHOLD_DAYS = 90;
export const LICENSE_EXPIRED_THRESHOLD_DAYS = 0;

export const PRIVACY_MASK_CHARACTER = '•';
export const PRIVACY_MASK_MIN_NAME_LENGTH = 3;
export const LOAN_NUMBER_MASK_PREFIX = '#••';

export const DOLLARS_BILLION = 1_000_000_000;
export const DOLLARS_MILLION = 1_000_000;
export const DOLLARS_THOUSAND = 1_000;

export const CommandCenterHomeTestIds = {
  PAGE: `${TestIdDomain.COMMAND_CENTER_HOME}.page`,
  // V7 narrative zone test IDs
  ZONE_RECAP: `${TestIdDomain.COMMAND_CENTER_HOME}.zone-recap`,
  ZONE_STATUS: `${TestIdDomain.COMMAND_CENTER_HOME}.zone-status`,
  ZONE_REFERENCE: `${TestIdDomain.COMMAND_CENTER_HOME}.zone-reference`,
  RECAP_TRIPTYCH: `${TestIdDomain.COMMAND_CENTER_HOME}.recap-triptych`,
  RECAP_PILLAR_EARNED: `${TestIdDomain.COMMAND_CENTER_HOME}.recap-pillar-earned`,
  RECAP_PILLAR_SPEED: `${TestIdDomain.COMMAND_CENTER_HOME}.recap-pillar-speed`,
  RECAP_PILLAR_RATES: `${TestIdDomain.COMMAND_CENTER_HOME}.recap-pillar-rates`,
  RECAP_EARNED_DISCLOSURE: `${TestIdDomain.COMMAND_CENTER_HOME}.recap-earned-disclosure`,
  RECAP_SPEED_LABEL: `${TestIdDomain.COMMAND_CENTER_HOME}.recap-speed-label`,
  RECAP_RATES_SOURCE: `${TestIdDomain.COMMAND_CENTER_HOME}.recap-rates-source`,
  SINCE_LAST_VISIT_RIBBON: `${TestIdDomain.COMMAND_CENTER_HOME}.since-last-visit-ribbon`,
  START_MY_DAY_CTA: `${TestIdDomain.COMMAND_CENTER_HOME}.start-my-day-cta`,
  TURN_TIME_BOARD: `${TestIdDomain.COMMAND_CENTER_HOME}.turn-time-board`,
  TURN_TIME_BOARD_ROW: `${TestIdDomain.COMMAND_CENTER_HOME}.turn-time-board-row`,
  TURN_TIME_BOARD_EMPTY: `${TestIdDomain.COMMAND_CENTER_HOME}.turn-time-board-empty`,
  TURN_TIME_BOARD_EMPTY_LINK: `${TestIdDomain.COMMAND_CENTER_HOME}.turn-time-board-empty-link`,
  PIPELINE_MILESTONE_LANES: `${TestIdDomain.COMMAND_CENTER_HOME}.pipeline-milestone-lanes`,
  PIPELINE_MILESTONE_EMPTY: `${TestIdDomain.COMMAND_CENTER_HOME}.pipeline-milestone-empty`,
  PIPELINE_SUBCOMPANY_BREAKDOWN: `${TestIdDomain.COMMAND_CENTER_HOME}.pipeline-subcompany-breakdown`,
  getPipelineSubCompanyBreakdownId: subCompanyName =>
    `${TestIdDomain.COMMAND_CENTER_HOME}.pipeline-subcompany-breakdown-${subCompanyName}`,
  ACTION_QUEUE: `${TestIdDomain.COMMAND_CENTER_HOME}.action-queue`,
  NEWS_ANNOUNCEMENTS: `${TestIdDomain.COMMAND_CENTER_HOME}.news-announcements`,
  HEADER: `${TestIdDomain.COMMAND_CENTER_HOME}.header`,
  LICENSE_LABEL: `${TestIdDomain.COMMAND_CENTER_HOME}.license-label`,
  LICENSE_EMPTY: `${TestIdDomain.COMMAND_CENTER_HOME}.license-empty`,
  getComplianceCredentialId: credentialId =>
    `${TestIdDomain.COMMAND_CENTER_HOME}.compliance-credential-${credentialId}`,
  COMP_PLAN_LABEL: `${TestIdDomain.COMMAND_CENTER_HOME}.comp-plan-label`,
  COMP_PLAN_DISCLOSURE: `${TestIdDomain.COMMAND_CENTER_HOME}.comp-plan-disclosure`,
  BOOK_TIME_BUTTON: `${TestIdDomain.COMMAND_CENTER_HOME}.book-time-button`,
  GRID: `${TestIdDomain.COMMAND_CENTER_HOME}.grid`,
  SUBMISSION_STATS_CARD: `${TestIdDomain.COMMAND_CENTER_HOME}.submission-stats-card`,
  // Legacy tier test-ids kept for backward compat with compliance tests
  TIER_1: `${TestIdDomain.COMMAND_CENTER_HOME}.tier-1`,
  TIER_2: `${TestIdDomain.COMMAND_CENTER_HOME}.tier-2`,
  TIER_3: `${TestIdDomain.COMMAND_CENTER_HOME}.tier-3`,
  TIER_4: `${TestIdDomain.COMMAND_CENTER_HOME}.tier-4`,
  TIER_5: `${TestIdDomain.COMMAND_CENTER_HOME}.tier-5`,
  KPI_HERO_ROW: `${TestIdDomain.COMMAND_CENTER_HOME}.kpi-hero-row`,
  MARKET_INTEL_STRIP: `${TestIdDomain.COMMAND_CENTER_HOME}.market-intel-strip`,
  ACTION_CENTER: `${TestIdDomain.COMMAND_CENTER_HOME}.action-center`,
  ANOMALY_ALERT_RAIL: `${TestIdDomain.COMMAND_CENTER_HOME}.anomaly-alert-rail`,
  PIPELINE_HEALTH_CARD: `${TestIdDomain.COMMAND_CENTER_HOME}.pipeline-health-card`,
  LOCK_EXPOSURE_CARD: `${TestIdDomain.COMMAND_CENTER_HOME}.lock-exposure-card`,
  LENDER_RELATIONSHIP_CARD: `${TestIdDomain.COMMAND_CENTER_HOME}.lender-relationship-card`,
  CHANNEL_WIRE_CARD: `${TestIdDomain.COMMAND_CENTER_HOME}.channel-wire-card`,
  APPROVAL_SCOPE_CARD: `${TestIdDomain.COMMAND_CENTER_HOME}.approval-scope-card`,
  APPROVAL_SCOPE_ROW: 'approval-scope.row',
  APPROVAL_SCOPE_PRODUCT_CHIP: 'approval-scope.product-chip',
  REQUEST_EXPANDED_APPROVAL_BUTTON: 'approval-scope.request-expanded-approval-button',
  SHOP_PERFORMANCE_CARD: `${TestIdDomain.COMMAND_CENTER_HOME}.shop-performance-card`,
  ACTION_CENTER_VIEW_ALL: `${TestIdDomain.COMMAND_CENTER_HOME}.action-center-view-all`,
  ACTION_QUEUE_VIEW_ALL: `${TestIdDomain.COMMAND_CENTER_HOME}.action-queue-view-all`,
  SHOP_PIPELINE_VIEW_ALL: `${TestIdDomain.COMMAND_CENTER_HOME}.shop-pipeline-view-all`,
  SHOP_PIPELINE_CARD: `${TestIdDomain.COMMAND_CENTER_HOME}.shop-pipeline-card`,
  SHOP_PIPELINE_TABLE: `${TestIdDomain.COMMAND_CENTER_HOME}.shop-pipeline-table`,
  SHOP_PIPELINE_EMPTY: `${TestIdDomain.COMMAND_CENTER_HOME}.shop-pipeline-empty`,
  SHOP_PIPELINE_LO_FILTER: 'shop-pipeline.lo-filter',
  SHOP_PIPELINE_MILESTONE_FILTER: 'shop-pipeline.milestone-filter',
  LO_SCOREBOARD: `${TestIdDomain.COMMAND_CENTER_HOME}.lo-scoreboard`,
  TRUST_FOOTER: `${TestIdDomain.COMMAND_CENTER_HOME}.trust-footer`,
  POLICY_LINK: 'trust-footer.policy-link',
  CLIENT_PROTECTION: `${TestIdDomain.COMMAND_CENTER_HOME}.client-protection`,
  CLIENT_PROTECTION_REPORT_LINK: 'client-protection.report-link',
  FILE_QUALITY: `${TestIdDomain.COMMAND_CENTER_HOME}.file-quality`,
  FILE_QUALITY_DISCLAIMER: `${TestIdDomain.COMMAND_CENTER_HOME}.file-quality-disclaimer`,
  getFileQualityMetricId: metricKey =>
    `${TestIdDomain.COMMAND_CENTER_HOME}.file-quality-metric-${metricKey}`,
  KPI_STRIP: `${TestIdDomain.COMMAND_CENTER_HOME}.kpi-strip`,
  PIPELINE_FUNNEL_ZONE: `${TestIdDomain.COMMAND_CENTER_HOME}.pipeline-funnel-zone`,
  TURN_TIME_TREND_ZONE: `${TestIdDomain.COMMAND_CENTER_HOME}.turn-time-trend-zone`,
  TURN_TIME_TREND_DEFINITION_SELECT: 'turn-time-trend.definition-select',
  TURN_TIME_STAGES_ZONE: `${TestIdDomain.COMMAND_CENTER_HOME}.turn-time-stages-zone`,
  TURN_TIME_STAGES_EMPTY: `${TestIdDomain.COMMAND_CENTER_HOME}.turn-time-stages-empty`,
  PRICING_BAND_ZONE: `${TestIdDomain.COMMAND_CENTER_HOME}.pricing-band-zone`,
  SLA_GAUGE_ZONE: `${TestIdDomain.COMMAND_CENTER_HOME}.sla-gauge-zone`,
  PRIORITY_QUEUE_ZONE: `${TestIdDomain.COMMAND_CENTER_HOME}.priority-queue-zone`,
  PRIORITY_QUEUE_VIEW_LINK: 'priority-queue.view-link',
  MARKET_SOURCE_LABEL: `${TestIdDomain.COMMAND_CENTER_HOME}.market-source-label`,
  LOCK_VS_FLOAT_SIGNAL: `${TestIdDomain.COMMAND_CENTER_HOME}.lock-vs-float-signal`,
  TURN_TIME_LOW_SAMPLE: `${TestIdDomain.COMMAND_CENTER_HOME}.turn-time-low-sample`,
  FALLOUT_N_LABEL: `${TestIdDomain.COMMAND_CENTER_HOME}.fallout-n-label`,
  STALE_BADGE: `${TestIdDomain.COMMAND_CENTER_HOME}.stale-badge`,
  NO_SOLICITATION_LINK: `${TestIdDomain.COMMAND_CENTER_HOME}.no-solicitation-link`,
  TIER_CHIP: `${TestIdDomain.COMMAND_CENTER_HOME}.tier-chip`,
  ACHIEVEMENTS_DRAWER: `${TestIdDomain.COMMAND_CENTER_HOME}.achievements-drawer`,
  GAMIFICATION_PROGRESS_BAND: `${TestIdDomain.COMMAND_CENTER_HOME}.gamification-progress-band`,
  ILLUSTRATIVE_BANNER: `${TestIdDomain.COMMAND_CENTER_HOME}.illustrative-banner`,
  CELEBRATION_TOAST: `${TestIdDomain.COMMAND_CENTER_HOME}.celebration-toast`,
  // Condition heat map (DEV-4173)
  CONDITION_HEATMAP_ZONE: `${TestIdDomain.COMMAND_CENTER_HOME}.condition-heatmap-zone`,
  CONDITION_HEATMAP_EMPTY: `${TestIdDomain.COMMAND_CENTER_HOME}.condition-heatmap-empty`,
  CONDITION_HEATMAP_TOTAL: `${TestIdDomain.COMMAND_CENTER_HOME}.condition-heatmap-total`,
  CONDITION_HEATMAP_FILES: `${TestIdDomain.COMMAND_CENTER_HOME}.condition-heatmap-files`,
  getConditionHeatmapCellId: (priorToType, ageBucket) =>
    `condition-heatmap.cell-${priorToType}-${ageBucket}`,
  getConditionHeatmapFilesSectionId: priorToType =>
    `condition-heatmap.files-${priorToType ?? 'all'}`,
  // Aging-by-category breakdown (DEV-5470)
  CONDITION_HEATMAP_CATEGORY: `${TestIdDomain.COMMAND_CENTER_HOME}.condition-heatmap-category`,
  getConditionHeatmapCategoryCellId: (categoryKey, ageBucket) =>
    `condition-heatmap.category-cell-${categoryKey}-${ageBucket}`,
  CONDITION_HEATMAP_VIEW_LINK: 'condition-heatmap.view-link',
  // Pipeline queue-depth + staff workload (DEV-4175)
  QUEUE_DEPTH_ZONE: `${TestIdDomain.COMMAND_CENTER_HOME}.queue-depth-zone`,
  QUEUE_DEPTH_EMPTY: `${TestIdDomain.COMMAND_CENTER_HOME}.queue-depth-empty`,
  QUEUE_DEPTH_STAFF_LIST: `${TestIdDomain.COMMAND_CENTER_HOME}.queue-depth-staff-list`,
  getQueueDepthStaffRowId: userId => `queue-depth.staff-row-${userId}`,
  QUEUE_DEPTH_VIEW_LINK: 'queue-depth.view-link',
  // EPO / clawback watch (DEV-4176)
  EPO_WATCH_ZONE: `${TestIdDomain.COMMAND_CENTER_HOME}.epo-watch-zone`,
  EPO_WATCH_EMPTY: `${TestIdDomain.COMMAND_CENTER_HOME}.epo-watch-empty`,
  getEpoWatchBucketId: label => `epo-watch.bucket-${label.replace(/\s+/g, '-')}`,
  // Disclosure / TRID timing monitor (DEV-4174)
  DISCLOSURE_TIMING_ZONE: `${TestIdDomain.COMMAND_CENTER_HOME}.disclosure-timing-zone`,
  DISCLOSURE_TIMING_EMPTY: `${TestIdDomain.COMMAND_CENTER_HOME}.disclosure-timing-empty`,
  DISCLOSURE_TIMING_OVERDUE: `${TestIdDomain.COMMAND_CENTER_HOME}.disclosure-timing-overdue`,
  // Closing-this-week strip + CD-not-sent reminder (DEV-4177)
  CLOSING_THIS_WEEK_ZONE: `${TestIdDomain.COMMAND_CENTER_HOME}.closing-this-week-zone`,
  CLOSING_THIS_WEEK_COUNT: `${TestIdDomain.COMMAND_CENTER_HOME}.closing-this-week-count`,
  CLOSING_THIS_WEEK_CD_NOT_SENT: `${TestIdDomain.COMMAND_CENTER_HOME}.closing-this-week-cd-not-sent`,
  CLOSING_THIS_WEEK_VIEW_LINK: 'closing-this-week.view-link',
};
